/**
 * Resolves the natural-position ids that map onto a tactical slot, for the
 * distinct-targets read that filters by position id (PV10, SE-8337).
 */
import { useCallback, useMemo } from "react";

import { POSITIONS_FULL_LIST_FIRST } from "@/config/constants";
import { isEligibleForSlot } from "@/features/scouting/utils/threeWeakest";
import {
  type TacticalSlot,
  useRosterManagementListPositionsQuery,
} from "@/generated/graphql";
import dedupePositionNodes from "@/utils/dedupePositionNodes";

/** Slot → position-id resolver backed by the full taxonomy. */
export interface SlotNaturalPositionIds {
  /** Ids of every taxonomy position whose abbreviation maps onto the slot. */
  readonly idsFor: (slot: TacticalSlot) => readonly string[];
  /** Whether the taxonomy has loaded. */
  readonly loaded: boolean;
}

/**
 * Reads the full position taxonomy once and maps slots onto position ids.
 * @param skip - Whether to skip the taxonomy read.
 * @returns Resolver and load state.
 */
const useSlotNaturalPositionIds = (skip: boolean): SlotNaturalPositionIds => {
  const { data } = useRosterManagementListPositionsQuery({
    // The backend's default page cuts the taxonomy short (SE-5481).
    variables: { first: POSITIONS_FULL_LIST_FIRST },
    skip,
  });
  const positions = useMemo(
    () => dedupePositionNodes(data?.listPositions?.edges),
    [data?.listPositions?.edges]
  );
  const idsFor = useCallback(
    (slot: TacticalSlot): readonly string[] =>
      positions
        .filter(
          position =>
            typeof position.abbreviation === "string" &&
            isEligibleForSlot([position.abbreviation], slot)
        )
        .map(position => position.id),
    [positions]
  );
  return { idsFor, loaded: positions.length > 0 };
};

export default useSlotNaturalPositionIds;
